import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { appeler, poserJeton, poserSurExpiration } from "../api/client";
import { EtatInvitationSchema, SessionSchema, type Session } from "../api/schemas";

type Contexte = {
  session: Session | null;
  connecterStaff: (nom: string, motDePasse: string) => Promise<void>;
  /** Ce que le lien permet. Ne connecte PAS : il n'ouvre plus de session. */
  verifierLien: (jeton: string) => Promise<{ etat: "ACreer" | "DejaCree"; nom: string | null }>;
  creerCompte: (jeton: string, identifiant: string, motDePasse: string) => Promise<void>;
  connecterClient: (identifiant: string, motDePasse: string) => Promise<void>;
  deconnecter: () => void;
};

const Ctx = createContext<Contexte | null>(null);
const CLE = "mdc.session";

export function FournisseurSession({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    const brut = sessionStorage.getItem(CLE);
    if (!brut) return null;
    const s = SessionSchema.safeParse(JSON.parse(brut));
    return s.success && new Date(s.data.expire) > new Date() ? s.data : null;
  });

  useEffect(() => {
    poserJeton(session?.accessToken ?? null);
    if (session) sessionStorage.setItem(CLE, JSON.stringify(session));
    else sessionStorage.removeItem(CLE);
  }, [session]);

  // Un 401 sur un appel porteur d'un jeton signifie que ce jeton ne vaut plus
  // rien. Effacer la session fait basculer le garde de routage, qui renvoie
  // chacun vers SON ecran de connexion — l'equipe et les clients n'ont pas le
  // meme.
  useEffect(() => {
    poserSurExpiration(() => setSession(null));
    return () => poserSurExpiration(null);
  }, []);

  const connecterStaff = useCallback(async (nom: string, motDePasse: string) => {
    setSession(
      await appeler("/auth/staff", SessionSchema, {
        method: "POST",
        body: JSON.stringify({ nom, motDePasse }),
      }),
    );
  }, []);

  // ⛔ Cette fonction ne pose AUCUNE session, et ce n'est pas un oubli.
  // Le lien dit seulement ce qu'il permet ; entrer exige des identifiants.
  const verifierLien = useCallback(async (jeton: string) => {
    return await appeler("/auth/portail", EtatInvitationSchema, {
      method: "POST",
      body: JSON.stringify({ jeton }),
    });
  }, []);

  const creerCompte = useCallback(
    async (jeton: string, identifiant: string, motDePasse: string) => {
      setSession(
        await appeler("/auth/portail/compte", SessionSchema, {
          method: "POST",
          body: JSON.stringify({ jeton, identifiant, motDePasse }),
        }),
      );
    },
    [],
  );

  const connecterClient = useCallback(async (identifiant: string, motDePasse: string) => {
    setSession(
      await appeler("/auth/client", SessionSchema, {
        method: "POST",
        body: JSON.stringify({ identifiant, motDePasse }),
      }),
    );
  }, []);

  const valeur = useMemo(
    () => ({
      session,
      connecterStaff,
      verifierLien,
      creerCompte,
      connecterClient,
      deconnecter: () => setSession(null),
    }),
    [session, connecterStaff, verifierLien, creerCompte, connecterClient],
  );

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useSession() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession hors FournisseurSession");
  return c;
}
